import { createColumnHelper } from "@tanstack/react-table";
import { groupBy } from "lodash";
import { useEffect, useMemo } from "react";
import { Alert, Form, InputGroup } from "react-bootstrap";
import { Path, useFieldArray, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import type {
  EditableHouseholdTrading,
  EditableHouseholdTradingAccounts,
  EditableHouseholdTradingHoldings,
} from "../../../../api/src/households-base/households-base.service";
import type { SecurityRestrictionTreatment } from "../../../../api/src/models/models.service";
import type { AccountHoldingWithSecurity } from "../../../../api/src/positions/positions.service";
import type { RiskAllocation } from "../../../../api/src/securities/securities.service";
import Loading from "../../Loading";
import FormFieldError from "../../components/FormFieldError";
import SecurityLink from "../../components/SecurityLink";
import { Table, useTable } from "../../components/Table/Table";
import { displayAccountName } from "../../lib/display";
import { formatCurrencyComponent, formatPercent } from "../../lib/numbers";
import { useQueryModels } from "../../models/lib";
import { getRiskAllocationDisplay } from "../../rebalances/lib";
import type { HouseholdWithAccountsAndRiskAllocation } from "./lib";

type AccountHoldingRow = {
  accountName?: string;
  accountGroupKey?: string;
  riskAllocation: RiskAllocation;
} & Pick<
  AccountHoldingWithSecurity,
  "id" | "value" | "security" | "unrealizedGainLoss"
> &
  EditableHouseholdTradingHoldings &
  EditableHouseholdTradingAccounts;

function HouseholdTradingHoldingsTable<TForm extends EditableHouseholdTrading>({
  holdings,
  accounts,
  isEditable = true,
}: {
  holdings: AccountHoldingWithSecurity[];
  accounts?: HouseholdWithAccountsAndRiskAllocation["accounts"];
  isEditable?: boolean;
}) {
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<EditableHouseholdTrading>();

  const { fields: holdingFields, update: updateHoldingField } = useFieldArray({
    name: "holdings",
    control,
    keyName: "_field_id",
  });

  // Create a map of symbol to holding field for more reliable lookup
  const holdingFieldsMap = useMemo(() => {
    const map = new Map();
    holdingFields.forEach((field) => {
      if (field.symbol) {
        // Use accountId + symbol as key to ensure uniqueness
        const key = `${field.accountId}_${field.symbol}`;
        map.set(key, field);
      }
    });
    return map;
  }, [holdingFields]);

  const {
    isPending: isPendingModels,
    isError: isErrorModels,
    data: dataModels,
  } = useQueryModels();

  const modelOptions = useMemo(
    () => [
      <option key={null} value="">
        None
      </option>,
      ...(dataModels ?? [])
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .map((model) => (
          <option key={model.id} value={model.id}>
            {model.name}
          </option>
        )),
    ],
    [dataModels],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<AccountHoldingRow>(),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("accountGroupKey", {
        header: "Account Name",
        cell: (info) => (
          <span className="fw-bold">
            <Link to={`/clients/accounts/${info.row.original.accountId}`}>
              {info.row.original.accountName ?? ""}
            </Link>
          </span>
        ),
        size: 230,
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.symbol, {
        header: "Allocation",
        id: "allocation",
        cell: (info) => (
          <SecurityLink
            symbol={info.getValue()}
            description={info.row.original.security?.description}
          />
        ),
        aggregatedCell: (info) => (
          <span className="fw-bold">
            {getRiskAllocationDisplay(info.row.original.riskAllocation)}
          </span>
        ),
        size: 180,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("value", {
        cell: (info) => (
          <div className="d-flex flex-column align-items-end">
            <div>{formatCurrencyComponent(info.getValue(), 2)}</div>
            <div style={{ fontSize: "0.8rem", height: "1.2em" }}>&nbsp;</div>
          </div>
        ),
        header: () => "Value",
        aggregatedCell: (info) => (
          <div className="d-flex flex-column align-items-end">
            <div className="fw-bold">
              {formatCurrencyComponent(info.getValue(), 2)}
            </div>
            <div style={{ fontSize: "0.8rem", height: "1.2em" }}>&nbsp;</div>
          </div>
        ),
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor("unrealizedGainLoss", {
        cell: (info) => {
          const unrealizedGain = info.getValue();
          const row = info.row.original;
          const costBasis = row.value - unrealizedGain;
          const percentage = costBasis > 0 ? unrealizedGain / costBasis : 0;

          return (
            <div className="d-flex flex-column align-items-end">
              <div>{formatCurrencyComponent(unrealizedGain, 2)}</div>
              <div
                className="small text-muted"
                style={{ fontSize: "0.8rem", fontStyle: "italic" }}
              >
                {formatPercent(percentage, 2)}
              </div>
            </div>
          );
        },
        header: () => "Unrealized Gain/Loss",
        aggregatedCell: (info) => {
          const unrealizedGain = info.getValue();
          const row = info.row.original;
          const costBasis = row.value - unrealizedGain;
          const percentage = costBasis > 0 ? unrealizedGain / costBasis : 0;

          return (
            <div className="d-flex flex-column align-items-end">
              <div className="fw-bold">
                {formatCurrencyComponent(unrealizedGain, 2)}
              </div>
              <div
                className="small text-muted fw-bold"
                style={{ fontSize: "0.8rem", fontStyle: "italic" }}
              >
                {formatPercent(percentage, 2)}
              </div>
            </div>
          );
        },
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor("restrictSecurity", {
        cell: (info) => {
          const row = info.row.original;
          const key = `${row.accountId}_${row.symbol}`;
          const holdingField =
            holdingFieldsMap.get(key) || holdingFields[info.row.index];

          // Find the correct index in the holdingFields array
          const fieldIndex = holdingFields.findIndex(
            (field) =>
              field.accountId === row.accountId && field.symbol === row.symbol,
          );

          return (
            <Form.Check
              type="checkbox"
              checked={holdingField.restrictSecurity}
              onChange={(val) => {
                const isChecked = val.currentTarget.checked;
                if (fieldIndex !== -1) {
                  updateHoldingField(fieldIndex, {
                    ...holdingFields[fieldIndex],
                    restrictSecurity: isChecked,
                    // If unchecking, you may want to reset treatment to default
                    // If checking, ensure it has a valid value
                    treatment: isChecked
                      ? holdingFields[fieldIndex].treatment || "sell_no_tax"
                      : "sell_no_tax",
                  });
                }
              }}
              disabled={!isEditable}
            />
          );
        },
        size: 120,
        header: () => "Restrict Security?",
        meta: {
          className: "text-center",
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor("treatment", {
        cell: (info) => {
          const row = info.row.original;
          const key = `${row.accountId}_${row.symbol}`;
          const holdingField =
            holdingFieldsMap.get(key) || holdingFields[info.row.index];

          // Find the correct index in the holdingFields array
          const fieldIndex = holdingFields.findIndex(
            (field) =>
              field.accountId === row.accountId && field.symbol === row.symbol,
          );

          return !holdingField.restrictSecurity ? null : (
            <Form.Select
              {...(fieldIndex !== -1
                ? register(`holdings.${fieldIndex}.treatment` as Path<TForm>)
                : {})}
              disabled={!isEditable}
              value={holdingField.treatment}
              onChange={(e) => {
                if (fieldIndex !== -1) {
                  updateHoldingField(fieldIndex, {
                    ...holdingFields[fieldIndex],
                    treatment: e.target.value as SecurityRestrictionTreatment,
                  });
                }
              }}
            >
              <option value="hold_only">Hold only</option>
              <option value="sell_no_tax">Sell if no tax consequences</option>
              <option value="exclude">Exclude</option>
            </Form.Select>
          );
        },
        header: () => "Treatment",
        size: 180,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("assignedModelId", {
        cell: () => null,
        aggregatedCell: (info) => (
          <Form.Select
            {...register(
              `accounts.${info.row.original.accountId}.assignedModelId` as Path<TForm>,
            )}
            disabled={!isEditable}
          >
            {modelOptions}
          </Form.Select>
        ),
        header: () => "Model",
        size: 420,
        enableColumnFilter: false,
      }),
    ],
    [
      columnHelper,
      errors.accounts,
      holdingFields,
      holdingFieldsMap,
      isEditable,
      modelOptions,
      register,
      updateHoldingField,
    ],
  );

  const holdingAccounts = useMemo(
    () => groupBy(holdings, "accountId"),
    [holdings],
  );
  const accountsWithHoldings = useMemo(
    () =>
      Object.entries(holdingAccounts).flatMap(([accountId, holdings]) => {
        const account = accounts?.find(
          (account) => account.id === parseInt(accountId),
        );

        return holdings.map((holding) => {
          const symbol = holding.security?.identifier ?? "";
          const securityRestriction = account?.securityRestrictions.find(
            (restriction) => restriction.symbol === symbol,
          );
          const restrictSecurity = typeof securityRestriction !== "undefined";

          const accountName = displayAccountName(
            account?.displayName,
            account?.displayNumber,
          );

          return {
            ...holding,
            accountName,
            accountGroupKey: `${account?.id}_${accountName}`,
            assignedModelId: account?.assignedModelId,
            riskAllocation: account?.riskAllocation?.current ?? {
              growth: 0.5,
              defensive: 0.5,
              unknown: 0,
            },
            restrictSecurity,
            symbol,
            treatment: securityRestriction?.treatment ?? "sell_no_tax",
          };
        });
      }),
    [accounts, holdingAccounts],
  );

  const { table } = useTable({
    columns,
    data: accountsWithHoldings,
    initialState: {
      sorting: [{ id: "value", desc: true }],
      grouping: ["accountGroupKey"],
      pagination: {
        pageSize: 9999,
      },
    },
    manualPagination: true,
    autoResetExpanded: false,
    getRowId: (row) => row.id.toString(),
  });

  useEffect(() => {
    if (accountsWithHoldings.length > 0) {
      table.toggleAllRowsExpanded(true);
    }
  }, [accountsWithHoldings, table]);

  return isPendingModels ? (
    <Loading message="Holdings" />
  ) : isErrorModels ? (
    <Alert variant="danger">An error occurred</Alert>
  ) : (
    <Table table={table} disablePagination isDisableGrouping isFixedGrouping />
  );
}

export default HouseholdTradingHoldingsTable;
